import { difference, union } from 'lodash';
import { fromPairs, groupBy, indexBy, pipe } from 'ramda';
import { Deleting, Empty, Loaded, Loading, RefreshableContent, Refreshing, Updating } from 'stores/core';
import { TaskLookupItem } from 'utils/to-store-task';
const EMPTY = {
    byId: {},
    byProjectId: {},
    ids: Empty(),
    projectIds: [],
    creating: {
        byId: {},
        ids: []
    }
};
const _creating = (creating_task, id)=>({ byId, byProjectId: { [creating_task.project]: tasksInProject = Empty(), ...byProjectId }, projectIds, ids, creating: { byId: creatingById, ids: creatingIds } })=>{
        const [, taskIds = []] = ids;
        const [, taskIdsInProject = []] = tasksInProject;
        const task_without_tree = {
            ...creating_task,
            taskId: -1 - creatingIds.length
        };
        const task = {
            ...task_without_tree,
            taskTree: [
                TaskLookupItem(task_without_tree)
            ]
        };
        return {
            byId: {
                ...byId,
                [task.taskId]: Loaded(task)
            },
            byProjectId: {
                ...byProjectId,
                [task.project]: Loaded(union(taskIdsInProject, [
                    task.taskId
                ]))
            },
            projectIds,
            ids: Loaded(union(taskIds, [
                task.taskId
            ])),
            creating: {
                byId: {
                    ...creatingById,
                    [id]: task.taskId
                },
                ids: union(creatingIds, [
                    id
                ])
            }
        };
    };
const added = (task)=>({ byId, byProjectId: { [task.project]: tasksInProject = Empty(), ...byProjectId }, projectIds, ids, creating })=>{
        const [, taskIds = []] = ids;
        const [, taskIdsInProject = []] = tasksInProject;
        return {
            byId: {
                ...byId,
                [task.taskId]: Loaded(task)
            },
            byProjectId: {
                ...byProjectId,
                [task.project]: Loaded(union(taskIdsInProject, [
                    task.taskId
                ]))
            },
            projectIds,
            ids: Loaded(union(taskIds, [
                task.taskId
            ])),
            creating
        };
    };
const created = (task, id)=>(store)=>{
        if (!store.creating.ids.includes(id)) {
            return added(task)(store);
        }
        const { byId, byProjectId: { [task.project]: tasksInProject = Empty(), ...byProjectId }, projectIds, ids, creating: { byId: creatingById, ids: creatingIds } } = store;
        const [, taskIds = []] = ids;
        const [, taskIdsInProject = []] = tasksInProject;
        const taskIdToRemove = creatingById[id];
        const { [taskIdToRemove]: creatingId, ...otherIds } = byId;
        const { [id]: creatingReference, ...otherCreatingReferences } = creatingById;
        return {
            byId: {
                ...otherIds,
                [task.taskId]: Loaded(task)
            },
            byProjectId: {
                ...byProjectId,
                [task.project]: Loaded(difference(union(taskIdsInProject, [
                    task.taskId
                ]), [
                    taskIdToRemove
                ]))
            },
            projectIds,
            ids: Loaded(difference(union(taskIds, [
                task.taskId
            ]), [
                taskIdToRemove
            ])),
            creating: {
                byId: otherCreatingReferences,
                ids: difference(creatingIds, [
                    id
                ])
            }
        };
    };
const loadingProjectTasks = ([, projectId])=>({ byId, byProjectId, projectIds, ids, creating })=>{
        var _byProjectId_projectId;
        const project = (_byProjectId_projectId = byProjectId[projectId]) !== null && _byProjectId_projectId !== void 0 ? _byProjectId_projectId : Empty();
        const next = project[0] === 'refreshing' || project[0] === 'loading' ? project : project[0] === 'loaded' ? Refreshing(project) : Loading();
        return {
            byId,
            byProjectId: {
                ...byProjectId,
                [projectId]: next
            },
            projectIds: union(projectIds, [
                projectId
            ]),
            ids,
            creating
        };
    };
const loadedAllTasks = (tasks)=>({ projectIds, creating })=>{
        const tasksByProjectId = pipe(()=>tasks, groupBy((x)=>x.project.toString()))();
        const byProjectId = fromPairs(projectIds.map((p)=>{
            var _tasksByProjectId_p;
            return [
                p,
                Loaded(((_tasksByProjectId_p = tasksByProjectId[p]) !== null && _tasksByProjectId_p !== void 0 ? _tasksByProjectId_p : []).map((t)=>t.taskId))
            ];
        }));
        const byId = pipe(()=>tasks.map(Loaded), indexBy((x)=>x[1].taskId))();
        const ids = Loaded(tasks.map((x)=>x.taskId));
        return {
            creating,
            projectIds,
            byProjectId,
            byId,
            ids
        };
    };
const loadedProjectTasks = ([, projectId], tasks)=>({ byId, byProjectId, projectIds, ids, creating })=>{
        const [, taskIds = []] = ids;
        return {
            byId: tasks.reduce((prev, task)=>({
                    ...prev,
                    [task.taskId]: Loaded(task)
                }), byId),
            byProjectId: {
                ...byProjectId,
                [projectId]: Loaded(tasks.map((task)=>task.taskId))
            },
            projectIds: union(projectIds, [
                projectId
            ]),
            ids: Loaded(union(taskIds, tasks.map((x)=>x.taskId))),
            creating
        };
    };
const _updating = (task)=>(store)=>{
        const { byId, byProjectId, projectIds, ids, creating } = store;
        const { [task.taskId]: [, content] } = byId;
        if (content == null) {
            return added(task)(store);
        }
        return {
            byId: {
                ...byId,
                [task.taskId]: Updating(task, content)
            },
            byProjectId,
            projectIds,
            ids,
            creating
        };
    };
const updating = (tasks)=>(store)=>tasks.reduce((state, task)=>_updating(task)(state), store);
const _updated = (task)=>(store)=>{
        var _byId_task_taskId;
        const { byId, byProjectId, projectIds, ids, creating } = store;
        const content = byId === null || byId === void 0 ? void 0 : (_byId_task_taskId = byId[task.taskId]) === null || _byId_task_taskId === void 0 ? void 0 : _byId_task_taskId[1];
        if (content == null) {
            return added(task)(store);
        }
        return {
            byId: {
                ...byId,
                [task.taskId]: Loaded(task)
            },
            byProjectId,
            projectIds,
            ids,
            creating
        };
    };
const updated = (tasks)=>(store)=>tasks.reduce((state, task)=>_updated(task)(state), store);
const _revert = (taskId)=>(store)=>{
        const { byId, byProjectId, projectIds, ids, creating } = store;
        const { [taskId]: task } = byId;
        if (task[0] !== 'updating') {
            return store;
        }
        return {
            byId: {
                ...byId,
                [taskId]: Loaded(task[2])
            },
            byProjectId,
            projectIds,
            ids,
            creating
        };
    };
const revert = (taskIds)=>(store)=>taskIds.reduce((state, taskId)=>_revert(taskId)(state), store);
const _deleting = (task)=>(store)=>{
        const { byId, byProjectId, projectIds, ids, creating } = store;
        const { [task.taskId]: [, content] } = byId;
        if (content == null) {
            return store;
        }
        return {
            byId: {
                ...byId,
                [task.taskId]: Deleting(task)
            },
            byProjectId,
            projectIds,
            ids,
            creating
        };
    };
const deleting = (tasks)=>(store)=>tasks.reduce((state, task)=>_deleting(task)(state), store);
const _deleted = (taskId)=>(store)=>{
        var _ids_, _byProjectId_task_project;
        const { byId, byProjectId, projectIds, ids, creating } = store;
        if (!((_ids_ = ids[1]) !== null && _ids_ !== void 0 ? _ids_ : []).includes(taskId)) {
            return store;
        }
        const { [taskId]: [, task], ...rest } = byId;
        const [, taskIds = []] = ids;
        if (task == null) {
            return store;
        }
        const lookup = (_byProjectId_task_project = byProjectId[task.project]) !== null && _byProjectId_task_project !== void 0 ? _byProjectId_task_project : Empty();
        return {
            byId: rest,
            byProjectId: lookup[0] === 'empty' || lookup[0] === 'loading' || lookup[0] === 'refreshing' ? byProjectId : {
                ...byProjectId,
                [task.project]: Loaded(lookup[1].filter((x)=>x !== task.taskId))
            },
            projectIds,
            ids: Loaded(taskIds.filter((x)=>x !== taskId)),
            creating
        };
    };
const deleted = (taskIds)=>(store)=>taskIds.reduce((state, taskId)=>_deleted(taskId)(state), store);
const _restored = (taskId)=>(store)=>{
        const { byId, byProjectId, projectIds, ids, creating } = store;
        const { [taskId]: task } = byId;
        if (task == null || task[0] !== 'deleting') {
            return store;
        }
        return {
            byId: {
                ...byId,
                [taskId]: Loaded(task[1])
            },
            byProjectId,
            projectIds,
            ids,
            creating
        };
    };
const restored = (taskIds)=>(store)=>taskIds.reduce((state, taskId)=>_restored(taskId)(state), store);
const failed = (id)=>(store)=>{
        const { creating: { byId, ids } } = store;
        const taskId = store.creating.byId[id];
        if (taskId == null) {
            return store;
        }
        const { [id]: failure, ...rest } = byId;
        return deleted([
            taskId
        ])({
            ...store,
            creating: {
                byId: rest,
                ids: difference(ids, [
                    id
                ])
            }
        });
    };
const ofProjectId = ([, projectId])=>({ byProjectId, byId })=>{
        var _byProjectId_projectId;
        return RefreshableContent.map((taskIds)=>taskIds.map((t)=>byId[t]).filter(Boolean).map(([, x])=>x).filter(Boolean))((_byProjectId_projectId = byProjectId[projectId]) !== null && _byProjectId_projectId !== void 0 ? _byProjectId_projectId : Empty());
    };
export const TasksStore = {
    empty: ()=>EMPTY,
    creating: _creating,
    created,
    failed,
    add: added,
    loadingProjectTasks,
    loadedProjectTasks,
    loadedAllTasks,
    updating,
    updated,
    revert,
    deleting,
    deleted,
    restored,
    ofProjectId
};
